import React, { useEffect, useRef, useState } from 'react';
import { Camera, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import API_URL from '../api';

function QRScanner() {
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(true);
  const scannerRef = useRef(null);
  const { darkMode } = useTheme();

  const bg = darkMode ? '#0f0f1a' : '#f0f2f5';
  const card = darkMode ? '#1a1a2e' : '#fff';
  const border = darkMode ? 'rgba(255,255,255,0.07)' : '#f0f0f0';
  const text = darkMode ? '#e0e0e0' : '#333';
  const subtext = darkMode ? '#888' : '#666';

  // eslint-disable-next-line
  useEffect(() => {
    const scanner = new Html5QrcodeScanner('qr-reader', { fps: 10, qrbox: { width: 250, height: 250 } }, false);

    scanner.render(
      async (decodedText) => {
        scanner.clear();
        setScanning(false);
        try {
          const data = JSON.parse(decodedText);
          const res = await axios.post(`${API_URL}/api/registrations/checkin`, {
            email: data.email,
            eventId: data.eventId
          });
          setResult({ success: true, message: res.data.message, email: data.email });
        } catch (err) {
          setResult({ success: false, message: err.response?.data?.error || 'Check-in failed!' });
        }
      },
      (err) => console.log('QR scan error:', err)
    );

    scannerRef.current = scanner;
    return () => scanner.clear().catch(() => {});
  }, []);

  const handleScanAgain = () => {
    setResult(null);
    setScanning(true);
    window.location.reload();
  };

  return (
    <div style={{ background: bg, minHeight: '100vh', padding: '30px 20px' }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
        #qr-reader { border: none !important; background: transparent !important; }
        #qr-reader__scan_region { background: ${darkMode ? '#161628' : '#f0f0f0'} !important; border-radius: 10px !important; }
        #qr-reader__dashboard { background: ${darkMode ? '#1a1a2e' : '#f8f8f8'} !important; border-radius: 0 0 10px 10px !important; padding: 15px !important; border: none !important; }
        #qr-reader__dashboard_section { background: ${darkMode ? '#1a1a2e' : '#f8f8f8'} !important; }
        #qr-reader__dashboard_section_csr { background: ${darkMode ? '#1a1a2e' : '#f8f8f8'} !important; }
        #qr-reader__dashboard_section_fsr { background: ${darkMode ? '#1a1a2e' : '#f8f8f8'} !important; }
        #qr-reader__status_span { color: ${subtext} !important; font-size: 13px !important; background: transparent !important; }
        #qr-reader__dashboard button {
          background: linear-gradient(135deg, #e94560, #c62a47) !important;
          color: #fff !important; border: none !important;
          border-radius: 8px !important; padding: 10px 20px !important;
          cursor: pointer !important; font-weight: 600 !important;
          font-size: 13px !important; margin: 5px !important;
        }
        #qr-reader__camera_selection {
          background: ${darkMode ? '#0f0f1a' : '#fff'} !important;
          color: ${text} !important;
          border: 1.5px solid ${darkMode ? 'rgba(255,255,255,0.1)' : '#e0e0e0'} !important;
          border-radius: 8px !important; padding: 8px !important;
          width: 100% !important; margin-bottom: 10px !important;
        }
        #qr-reader a { color: #e94560 !important; background: transparent !important; }
        #qr-reader__dashboard_section_swaplink { color: #e94560 !important; background: transparent !important; font-size: 13px !important; }
        #qr-reader img { filter: ${darkMode ? 'invert(0.8)' : 'none'} !important; }
        #qr-reader__filescan_input { color: ${text} !important; }
        span[id^="qr-reader"] { background: transparent !important; color: ${text} !important; }
        div[id^="qr-reader"] { background: transparent !important; }
      `}</style>

      <div style={{ maxWidth: '520px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '25px', textAlign: 'center' }}>
          <div style={{ background: 'rgba(233,69,96,0.1)', borderRadius: '50%', width: '65px', height: '65px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px', border: '1px solid rgba(233,69,96,0.2)' }}>
            <Camera size={30} color="#e94560" />
          </div>
          <h1 style={{ color: text, fontSize: '26px', fontWeight: '700' }}>QR Scanner</h1>
          <p style={{ color: subtext, marginTop: '5px', fontSize: '14px' }}>Scan student's QR code to check them in</p>
        </div>

        {/* Scanner Box */}
        {scanning && (
          <div style={{ background: card, borderRadius: '20px', padding: '20px', border: `1px solid ${border}`, boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.08)', animation: 'fadeIn 0.4s ease' }}>
            <div id="qr-reader" style={{ width: '100%' }} />
          </div>
        )}

        {/* Result */}
        {result && (
          <div style={{ background: card, borderRadius: '20px', padding: '40px', border: `1px solid ${border}`, textAlign: 'center', animation: 'fadeIn 0.4s ease', boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.08)' }}>
            <div style={{ animation: 'pulse 0.5s ease', marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
              {result.success
                ? <CheckCircle size={70} color="#4caf50" />
                : <XCircle size={70} color="#e94560" />
              }
            </div>
            <h2 style={{ color: result.success ? '#4caf50' : '#e94560', fontSize: '22px', fontWeight: '700', marginBottom: '10px' }}>
              {result.success ? 'Check-in Successful!' : 'Check-in Failed!'}
            </h2>
            <p style={{ color: subtext, marginBottom: '15px', fontSize: '15px' }}>{result.message}</p>
            {result.email && (
              <div style={{ background: darkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5', borderRadius: '10px', padding: '12px', marginBottom: '25px' }}>
                <p style={{ color: text, fontSize: '14px', margin: 0 }}>📧 {result.email}</p>
              </div>
            )}
            <button onClick={handleScanAgain} style={{ background: 'linear-gradient(135deg, #e94560, #c62a47)', color: '#fff', padding: '14px 35px', borderRadius: '12px', fontSize: '15px', display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 15px rgba(233,69,96,0.3)' }}>
              <RefreshCw size={16} /> Scan Next Student
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default QRScanner;